import { Box, Button, Stack, Typography } from '@mui/material'
import { useState } from 'react'

import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { Loading } from '@/components/organisms/loading'
import {
  CreateCryptoWalletConfirmModal,
  CryptoWalletAddress,
  CryptoWalletBalance,
  CryptoWalletConnectDrawer,
  MetamaskAccountIcon,
} from '@/components/organisms/nft'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { captureError } from '@/core/errors'

export * from './fragments'

export type State = {
  loading: boolean
  artistId: string
  cryptoWallet: {
    displayName: string
    walletAddress: string
    balance: string
  }
}

export type Action = {
  connectMetamask: () => Promise<void>
  connectWeb3auth: () => Promise<void>
}

export const ConsoleCryptocurrencyWalletTemplate: Template<State, Action> = ({
  state,
  connectMetamask,
  connectWeb3auth = async () => {},
}) => {
  const { t } = useTranslation('wallet')
  const { routerNames } = useRouter()

  const [open, setOpen] = useState(false)
  const [openWalletCreateConfirmModal, setOpenWalletCreateConfirmModal] =
    useState(false)

  const { showError, showMessage } = useMessage()
  const [connecting, setConnecting] = useState(false)

  const onConnectMetamask = () => {
    setConnecting(true)
    return connectMetamask()
      .then(() => {
        showMessage(t('connected'), 'success')
      })
      .catch(e => {
        captureError(e, 'ConsoleCryptocurrencyWalletTemplate')
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpen(false)
      })
  }

  const onConnectWeb3auth = () => {
    setConnecting(true)
    return connectWeb3auth()
      .catch(e => {
        captureError(e, 'ConsoleCryptocurrencyWalletTemplate')
        showError(e)
      })
      .finally(() => {
        setConnecting(false)
        setOpen(false)
        setOpenWalletCreateConfirmModal(false)
      })
  }

  return (
    <ArtistThemeProvider artistId={state.artistId || ''}>
      <ConsoleBaseLayout
        margin
        header={{
          title: t('connectWallet'),
          back: true,
        }}
      >
        <Head title={routerNames.ConsoleCryptocurrencyWallet} />

        <Box sx={{ py: 5 }}>
          {state.loading ? (
            <LoadingCell />
          ) : state.cryptoWallet.walletAddress ? (
            <WalletConnectCell cryptoWallet={state.cryptoWallet} />
          ) : (
            <WalletNotConnectedCell onConnectWallet={() => setOpen(true)} />
          )}
        </Box>

        <CryptoWalletConnectDrawer
          open={open}
          connecting={connecting}
          onClose={() => setOpen(false)}
          onConnectWallet={walletType => {
            switch (walletType) {
              case 'web3auth':
                return setOpenWalletCreateConfirmModal(true)
              case 'metamask':
                return onConnectMetamask()
            }
          }}
        />

        <CreateCryptoWalletConfirmModal
          open={openWalletCreateConfirmModal}
          loading={connecting}
          onClose={() => setOpenWalletCreateConfirmModal(false)}
          onCreate={onConnectWeb3auth}
        />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const WalletConnectCell: Cell<{ cryptoWallet: State['cryptoWallet'] }> = ({
  cryptoWallet,
}) => {
  const { t } = useTranslation('wallet')

  return (
    <CellFrame>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 5,
          borderRadius: 2,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {t('connectWallet')}
        </Typography>
        <MetamaskAccountIcon walletAddress={cryptoWallet.walletAddress || ''} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {cryptoWallet.displayName}
        </Typography>
        <CryptoWalletAddress cryptoWalletAddress={cryptoWallet.walletAddress} />
        <Stack sx={{ mt: 4 }}>
          <CryptoWalletBalance cryptoWalletBalance={cryptoWallet.balance} />
        </Stack>
      </Stack>
    </CellFrame>
  )
}

const WalletNotConnectedCell: Cell<{
  onConnectWallet: () => void
}> = ({ onConnectWallet }) => {
  const { t } = useTranslation('wallet')

  return (
    <CellFrame>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        sx={{
          py: 5,
          borderRadius: 2,
          backgroundColor: 'grey.50',
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
          {t('connectWallet')}
        </Typography>
        <Stack direction="row" sx={{ px: 7, mb: 3 }}>
          <Typography variant="caption">
            {t('artistConnectWalletDescription')}
          </Typography>
        </Stack>
        <Button onClick={onConnectWallet} sx={{ width: 88 }}>
          {t('connect')}
        </Button>
      </Stack>
    </CellFrame>
  )
}

const LoadingCell = () => {
  return (
    <CellFrame>
      <Stack
        sx={{
          height: 200,
          width: '100%',
          bgcolor: 'grey.50',
          borderRadius: 2,
          py: 5,
        }}
        justifyContent="center"
        alignItems="center"
      >
        <Loading />
      </Stack>
    </CellFrame>
  )
}
